import {
  GeneralCommunicationSection,
  GeneralDisplaySilentModeEndpointRestartPopup,
  GeneralDisplaySilentModeIssuesVisibility,
  GeneralDisplaySilentModeShowPopups,
  GeneralSecurityTelemetrySection,
  GeneralUpdateLocation,
  GeneralUpdateSection,
  Inheritance,
  SectionId,
} from '@nirvana/shared/dtos/types/policies/common';

export interface GeneralSecurityTelemetryFormValue {
  uiSettings: { general: { securityTelemetry: GeneralSecurityTelemetrySection } };
}

export interface GeneralCommunicationFormValue {
  uiSettings: { general: { communication: GeneralCommunicationSection } };
}

// notifications
export interface GeneralNotificationsFormValue {
  uiSettings: {
    general: {
      display: {
        silentMode: {
          enable: boolean;
          iconNotificationArea: boolean;
          showPopups: GeneralDisplaySilentModeShowPopups;
          displayAlertsPopups: boolean;
          issuesVisibility: GeneralDisplaySilentModeIssuesVisibility;
          endpointRestartPopup: GeneralDisplaySilentModeEndpointRestartPopup;
        };
      };
    };
  };
}

// inheritance
export interface GeneralInheritanceFormValue {
  inheritance: Inheritance[];
}

export interface InheritanceItemFormValue extends Inheritance {
  sectionIds: (SectionId | null)[];
}

// update
export enum GeneralUpdateSchedulerType {
  PRODUCT = 'product',
  SIGNATURE = 'signature',
}
export interface GeneralUpdateFormValue {
  uiSettings: {
    general: {
      update: GeneralUpdateSection;
    };
  };
}

export interface GeneralUpdateLocationFormValue extends GeneralUpdateLocation {
  dbServerValue?: string;
}

export interface GeneralAdvancedFormValue {
  uiSettings: {
    general: {
      advanced: {
        passwordConfig: { profile: number; value: string };
        powerUser: { enabled: boolean; password: string };
        settings: {
          removeEvents: { settings: { days: number } };
          reportActiveSessions: boolean;
          sendTelemetry: boolean;
          submitReports: boolean;
          submitSuspicious: boolean;
          useGlobalProtectiveNetwork: boolean;
        };
      };
      update: {
        settings: {
          proxy: {
            enable: boolean;
            profile: number;
            settings: {
              server: string;
              port: string;
              username: string;
              password: string;
            };
          };
        };
      };
    };
  };
}

export interface GeneralDetailsFormValue {
  name: string;
  lastModified: string;
  ownerFullName: string;
  created: string;
  uiSettings: {
    general: {
      allowChangeByOtherUsers: boolean;
      display: {
        supportInformation: {
          website: string;
          email: string;
          phone: string;
        };
      };
    };
  };
}

export enum GeneralSecurityTelemetrySearchableKeywords {
  SecurityTelemetry = 'SecurityTelemetry',
  SiemConnectionSettings = 'SiemConnectionSettings',
  SiemSolution = 'SiemSolution',
  ServerUrl = 'ServerUrl',
  Token = 'Token',
  HostnameIp = 'HostnameIp',
  Port = 'Port',
  IgnoreSslErrors = 'IgnoreSslErrors',
  EventTypes = 'EventTypes',
  ProcessCreate = 'ProcessCreate',
  TerminateProcess = 'TerminateProcess',
  NetworkConnection = 'NetworkConnection',
  Logon = 'Logon',
  Logoff = 'Logoff',
  CreatFile = 'CreatFile',
  DeleteFile = 'DeleteFile',
  ModifyFile = 'ModifyFile',
  ReadFromFile = 'ReadFromFile',
  MoveFile = 'MoveFile',
  RegistryCreateKey = 'RegistryCreateKey',
  RegistryDeleteKey = 'RegistryDeleteKey',
  RegistryModifyValue = 'RegistryModifyValue',
  RegistryDeleteValue = 'RegistryDeleteValue',
  CommunicationBetweenEndpointsSIEM = 'CommunicationBetweenEndpointsSIEM',
  ProxyConfigured = 'ProxyConfigured',
  DoNotUseProxy = 'DoNotUseProxy',
}

export enum GeneralCommunicationSearchableKeywords {
  Communication = 'Communication',
  EndpointCommunicationAssignment = 'EndpointCommunicationAssignment',
  UsePreviousSettings = 'UsePreviousSettings',
  UseProxyDefinedInSettings = 'UseProxyDefinedInSettings',
  DoNotUseProxy = 'DoNotUseProxy',
  CommunicationBetweenEndpointsAndCloudServices = 'CommunicationBetweenEndpointsAndCloudServices',
  CommunicationBetweenEndpointsAndRelays = 'CommunicationBetweenEndpointsAndRelays',
  RemoteShellConnectionWithEndpoint = 'RemoteShellConnectionWithEndpoint',
  AutoDetectProxySettings = 'AutoDetectProxySettings',
}

export enum GeneralDetailsSearchableKeywords {
  PolicyDetails = 'PolicyDetails',
  Name = 'Name',
  AllowChangeByOtherUsers = 'AllowChangeByOtherUsers',
  History = 'History',
  CreatedBy = 'CreatedBy',
  CreatedOn = 'CreatedOn',
  ModifiedOn = 'ModifiedOn',
  TechnicalSupportInformation = 'TechnicalSupportInformation',
  Website = 'Website',
  Email = 'Email',
  Phone = 'Phone',
}

export enum GeneralAdvancedSearchableKeywords {
  Settings = 'Settings',
  UninstallPasswordConfiguration = 'UninstallPasswordConfiguration',
  KeepInstallationSettings = 'KeepInstallationSettings',
  SetUninstallPassword = 'SetUninstallPassword',
  Password = 'Password',
  RetypePassword = 'RetypePassword',
  DisablePassword = 'DisablePassword',
  ProxyConfiguration = 'ProxyConfiguration',
  Server = 'Server',
  Port = 'Port',
  Username = 'Username',
  PowerUser = 'PowerUser',
  Options = 'Options',
  RemoveEventsOlderThan = 'RemoveEventsOlderThan',
  SubmitCrashReportsToBitdefender = 'SubmitCrashReportsToBitdefender',
  SubmitSuspiciousFilesForAnalysis = 'SubmitSuspiciousFilesForAnalysis',
  SendFeedbackRegardingSecurityAgentsHealth = 'SendFeedbackRegardingSecurityAgentsHealth',
  UseBitdefenderGlobalProtectiveNetwork = 'UseBitdefenderGlobalProtectiveNetwork',
  AllowEndpointToSendUserLoginDataToGZ = 'AllowEndpointToSendUserLoginDataToGZ',
}

export enum GeneralNotificationsSearchableKeywords {
  Notifications = 'Notifications',
  SilentMode = 'SilentMode',
  ShowIconInNotificationArea = 'ShowIconInNotificationArea',
  DisplayAlertsPopups = 'DisplayAlertsPopups',
  DisplayNotificationPopups = 'DisplayNotificationPopups',
  EndpointIssuesVisibility = 'EndpointIssuesVisibility',
  EndpointRestartNotification = 'EndpointRestartNotification',
}

export enum GeneralInheritanceRulesSearchableKeywords {
  InheritanceRules = 'InheritanceRules',
  AddInheritanceRules = 'AddInheritanceRules',
}

export enum GeneralUpdateSearchableKeywords {
  ProductUpdate = 'ProductUpdate',
  Scheduler = 'Scheduler',
  Recurrence = 'Recurrence',
  PostponeReboot = 'PostponeReboot',
  Reboot = 'Reboot',
  SecurityUpdate = 'SecurityUpdate',
  UpdateLocations = 'UpdateLocations',
  UseBitdefender = 'UseBitdefender',
  UpdateRing = 'UpdateRing',
}
